import { useAuth } from '@/contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { RefreshCw, User, Settings } from 'lucide-react';
import { useGlobalRefresh } from '@/contexts/GlobalRefreshContext';
import { useQuery } from '@tanstack/react-query';
import { launchConfigService } from '@/services/launch-config.service';
import { useMemo, useEffect, useState } from 'react';

const Header = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { refreshing, handleRefresh } = useGlobalRefresh();
  const [forceUpdate, setForceUpdate] = useState(0);

  // Force re-render when user roles change
  useEffect(() => {
    if (user) {
      setForceUpdate(prev => prev + 1);
    }
  }, [user?.is_admin, user?.is_super_admin, user?.roles]);

  // Listen for storage events to sync with localStorage changes (e.g., after login)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userData' && e.newValue) {
        try {
          const userData = JSON.parse(e.newValue);
          const hasAdminRole = userData.is_admin || userData.is_super_admin || 
            userData.roles?.some((role: string) => role === 'admin' || role === 'superadmin');
          const currentUserHasAdmin = user?.is_admin || user?.is_super_admin || 
            user?.roles?.some(role => role === 'admin' || role === 'superadmin');
          
          // If localStorage has different admin status than context, force update
          if (hasAdminRole !== currentUserHasAdmin) {
            setForceUpdate(prev => prev + 1);
          }
        } catch (e) {
          // Ignore errors
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event that we can dispatch after login
    const handleUserDataUpdate = () => {
      setForceUpdate(prev => prev + 1);
    };
    
    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
    };
  }, [user?.is_admin, user?.is_super_admin, user?.roles]);

  const displayName =
    user?.username?.includes('@')
      ? user.username.split('@')[0]
      : user?.username;

  const { data: valuationDate, isLoading } = useQuery<string>({
    queryKey: ['valuation-date'],
    queryFn: () => launchConfigService.getValuationDate(),
    retry: 1,
    retryDelay: 1000,
  });

  const isActive = (path: string) => {
    // Handle edit routes for loan form
    if (
      path === '/loan-form' &&
      location.pathname.startsWith('/loan-form/edit/')
    ) {
      return true;
    }
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
  };

  const handleRefreshClick = () => {
    if (valuationDate) {
      handleRefresh(valuationDate);
    }
  };

  const navItems = [
    { path: '/client', label: 'Client' },
    { path: '/loans', label: 'Loan Portfolio' },
    // { path: '/loan-form', label: 'Loan Details' }, // Hidden
    { path: '/dashboard', label: 'Dashboard Analytics' },
    { path: '/valuation-reports', label: 'Valuation Reports' },
    // { path: '/loan-analytics', label: 'Analytics' }, // Hidden from navigation
    { path: '/credit-spreads', label: 'Market Color' },
    { path: '/benchmark-rates', label: 'Benchmark Rates' },
    // { path: '/fx-rates', label: 'FX Rates' }, // Hidden from navigation
    // { path: '/rpx-adjustments', label: 'RPX Adjustments' }, // Hidden from navigation
    // { path: '/pricing-comparison', label: 'Comparison' }, // Hidden - API support not ready
  ];

  return (
    <header
      className="bg-white shadow-sm border-b"
      data-test-id="layout-header"
    >
      <div className="container mx-auto px-4" data-test-id="header-container">
        <div
          className="flex items-center justify-between h-20"
          data-test-id="header-content"
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center justify-center space-x-2 shrink-0"
          >
            <img
              src="/logo.png"
              alt="RPX"
              className="h-20 w-auto hidden lg:block"
            />
            <img
              src="/small-logo.png"
              alt="RPX"
              className="h-14 w-auto block lg:hidden"
            />
          </Link>
          {/* Navigation */}
          <nav
            className="hidden md:flex space-x-3"
            data-test-id="main-navigation"
          >
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`my-auto px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 text-center ${
                  isActive(item.path)
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                data-test-id={`nav-link-${item.path.replace('/', '')}`}
              >
                {item.label.split(' ').map((word, idx) => (
                  <span key={idx} className="block leading-tight">
                    {word}
                  </span>
                ))}
              </Link>
            ))}
          </nav>
          <div className="flex items-center space-x-4 flex-nowrap">
            {/* Refresh button - visible on all pages */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshClick}
              disabled={refreshing || isLoading}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              data-test-id="header-refresh-button"
            >
              <RefreshCw
                className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`}
                data-test-id="header-refresh-icon"
              />
              Refresh
            </Button>
            <span
              className="max-w-[180px] truncate text-sm text-gray-600"
              title={user?.username}
            >
              Welcome, {displayName}
            </span>
            {useMemo(() => {
              // Check if user is admin or superadmin
              const isAdmin = user?.is_admin || user?.is_super_admin || 
                user?.roles?.some(role => role === 'admin' || role === 'superadmin');
              
              // Force re-render by including forceUpdate in dependency
              void forceUpdate;
              
              return isAdmin ? (
                <Link to="/admin" key={`admin-link-${forceUpdate}`}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-gray-600 hover:text-gray-900"
                    data-test-id="admin-button"
                  >
                    <User className="h-4 w-4" />
                  </Button>
                </Link>
              ) : (
                <Link to="/profile" key={`profile-link-${forceUpdate}`}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-gray-600 hover:text-gray-900"
                    data-test-id="profile-button"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </Link>
              );
            }, [user?.is_admin, user?.is_super_admin, user?.roles, forceUpdate])}
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="text-gray-600 hover:text-gray-900"
            >
              Logout
            </Button>
          </div>
          {/* Mobile menu button */}
          <div className="md:hidden" data-test-id="mobile-menu-wrapper">
            <button
              className="text-gray-600 hover:text-gray-900"
              data-test-id="mobile-menu-button"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                data-test-id="mobile-menu-icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                  data-test-id="mobile-menu-icon-path"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
